import { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { CustomButton, SnackBar } from "src/design";
import { colorTextMuted, colorTextPrimary } from "src/design/tokens";
import { ConfigurationContext } from "src/Utils/Context/ConfigurationContext";
import { FeatureSwitch } from "src/business/configuration/featureSwitch";
import {
  useCreateFeatureSwitch,
  useUpdateFeatureSwitch,
} from "src/data/admin/queries/featureSwitch";
import { CreateFeatureSwitchRequest } from "src/data/admin/types/featureSwitch";
import { FeatureSwitchesTable } from "./components/FeatureSwitchesTable";
import { CreateFeatureSwitchDialog } from "./components/CreateFeatureSwitchDialog";

interface Snack {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

const CLOSED_SNACK: Snack = { open: false, message: "", severity: "success" };

const FeatureSwitches = () => {
  const { configuration, loading, refreshConfiguration } =
    useContext(ConfigurationContext);

  /**
   * The list is owned by the configuration, but mutations answer with the
   * updated switch — mirroring it locally keeps the toggle responsive instead of
   * waiting a round-trip for the configuration refresh to land. The effect below
   * reconciles back to server truth whenever the configuration re-resolves.
   */
  const [featureSwitches, setFeatureSwitches] = useState<FeatureSwitch[]>(
    configuration.featureSwitches,
  );
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack>(CLOSED_SNACK);

  useEffect(() => {
    setFeatureSwitches(configuration.featureSwitches);
  }, [configuration.featureSwitches]);

  const showError = (error: unknown, fallback: string) =>
    setSnack({
      open: true,
      message: error instanceof Error ? error.message : fallback,
      severity: "error",
    });

  const createMutation = useCreateFeatureSwitch((created) => {
    setFeatureSwitches((current) => [...current, created]);
    setCreateOpen(false);
    setSnack({
      open: true,
      message: `Feature switch "${created.name}" created.`,
      severity: "success",
    });
    refreshConfiguration();
  });

  const updateMutation = useUpdateFeatureSwitch((updated) => {
    setFeatureSwitches((current) =>
      current.map((featureSwitch) =>
        featureSwitch.id === updated.id ? updated : featureSwitch,
      ),
    );
    setPendingId(null);
    setSnack({
      open: true,
      message: `"${updated.name}" turned ${updated.active ? "on" : "off"}.`,
      severity: "success",
    });
    refreshConfiguration();
  });

  const handleToggle = (featureSwitch: FeatureSwitch, active: boolean) => {
    setPendingId(featureSwitch.id);
    updateMutation.mutate(
      { id: featureSwitch.id, active },
      {
        onError: (error) => {
          setPendingId(null);
          showError(error, "Failed to update feature switch");
        },
      },
    );
  };

  const handleCreate = (payload: CreateFeatureSwitchRequest) =>
    createMutation.mutate(payload, {
      onError: (error) => showError(error, "Failed to create feature switch"),
    });

  const filtered = featureSwitches.filter((featureSwitch) =>
    featureSwitch.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <Box>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Grid item xs={12} md="auto">
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              Feature Switches
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto">
            <CustomButton
              color="black"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              New Feature Switch
            </CustomButton>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search feature switches…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: colorTextMuted, fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <FeatureSwitchesTable
                featureSwitches={filtered}
                pendingId={pendingId}
                filtered={search.length > 0}
                onToggle={handleToggle}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      <CreateFeatureSwitchDialog
        open={createOpen}
        loading={createMutation.isPending}
        existingNames={featureSwitches.map((featureSwitch) => featureSwitch.name)}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((current) => ({ ...current, open: false }))}
      />
    </>
  );
};

export default FeatureSwitches;
